import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@workos-inc/authkit-react';

import { accountApi, type Me } from './accountApi';
import { MeContext, type MeStatus } from './meContext';

type Fetched = { userId: string; me: Me } | { userId: string; error: string };

export default function MeProvider({ children }: { children: ReactNode }) {
  const { isLoading, user, getAccessToken } = useAuth();
  const userId = user?.id ?? null;
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [attempt, setAttempt] = useState(0);

  // `getAccessToken` changes identity when AuthKit's client arrives; the API
  // object follows it so a call never goes out with the stand-in.
  const api = useMemo(() => accountApi(() => getAccessToken()), [getAccessToken]);

  useEffect(() => {
    if (isLoading || !userId) return;
    let cancelled = false;
    api
      .me()
      .then((me) => {
        if (!cancelled) setFetched({ userId, me });
      })
      .catch((err: unknown) => {
        if (!cancelled) setFetched({ userId, error: err instanceof Error ? err.message : 'Unknown error' });
      });
    return () => {
      cancelled = true;
    };
  }, [api, isLoading, userId, attempt]);

  const status: MeStatus = useMemo(() => {
    if (isLoading) return { kind: 'auth-loading' };
    if (!userId) return { kind: 'logged-out' };
    // An answer for someone else (a different login in this tab) is no answer.
    if (!fetched || fetched.userId !== userId) return { kind: 'loading' };
    return 'me' in fetched ? { kind: 'ready', me: fetched.me } : { kind: 'error', message: fetched.error };
  }, [isLoading, userId, fetched]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  const markOnboardingComplete = useCallback(() => {
    setFetched((prev) =>
      prev && 'me' in prev ? { ...prev, me: { ...prev.me, onboardingComplete: true } } : prev,
    );
  }, []);

  const value = useMemo(
    () => ({ status, api, refresh, markOnboardingComplete }),
    [status, api, refresh, markOnboardingComplete],
  );

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}
